import { useEffect, useRef, useState } from 'react';
import * as S from './SpeakingDescribeImage.styles';
import { STAGES } from '@app/constants/stages';
import { Recording } from '../Recording';
import { Answer } from '../../Answer';
import { WaitingTime } from '../WaitingTime';
import VoiceIndicator from '../Recording/VoiceIndicator';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@app/store/store';
import { setAllowNext } from '@app/store/slices/mockTestSlice';

interface Props {
  prepTime: number;
  recordingTime: number;
  showAnswer: boolean;
  imageUrl: string;
  questionNumber: string;
  canStart: boolean;
  onAnswerChange?: (audio: Blob | string) => void;
}

export const SpeakingDescribeImage: React.FC<Props> = ({
  prepTime,
  recordingTime,
  showAnswer,
  imageUrl,
  questionNumber,
  canStart,
  onAnswerChange,
}) => {
  const dispatch = useDispatch();
  const { isCompleteAudio } = useSelector((state: RootState) => state.mockTest);

  const recordingRef = useRef<{ startRecording: () => void; stopRecording: () => void }>(null);
  const waitingTimeRef = useRef<{ finishImmediately: () => void } | null>(null);
  const [stage, setStage] = useState(STAGES.START);
  const [audioURL, setAudioURL] = useState('');
  const [audioBlod, setAudioBlod] = useState<Blob>();

  useEffect(() => {
    handleStopRecording();
    setStage(STAGES.START);
  }, [questionNumber]);

  useEffect(() => {
    if (!showAnswer) return;
    if (stage == STAGES.START) {
      setStage(STAGES.COMPLETE);
    } else {
      handleStopRecording();
    }
  }, [showAnswer]);

  useEffect(() => {
    if (!isCompleteAudio) return;
    handleStopRecording();
  }, [isCompleteAudio]);

  const handleStart = () => {
    if (stage === STAGES.RECORDING) {
      handleStopRecording();
      return;
    }
    dispatch(setAllowNext(true));

    waitingTimeRef.current?.finishImmediately();
    setStage(STAGES.RECORDING);
    recordingRef.current?.startRecording();
    onAnswerChange && onAnswerChange('');
  };

  const handleComplete = async (audio: string) => {
    setStage(STAGES.COMPLETE);
    setAudioURL(audio);

    const blob = await fetch(audio).then((res) => res.blob());
    setAudioBlod(blob);
    onAnswerChange && onAnswerChange(blob);
  };

  const handleStopRecording = () => {
    if (recordingRef.current) {
      recordingRef.current.stopRecording();
    }
  };

  return (
    <>
      <S.Wrapper>
        <S.Column>
          <img alt="Image" src={imageUrl} />
        </S.Column>
        <S.Column>
          <S.Border>
            <div className="flex">
              <WaitingTime ref={waitingTimeRef} onStart={handleStart} waitingTime={prepTime} key={questionNumber} />
              <S.VoiceIndicator>{stage === STAGES.RECORDING && <VoiceIndicator isRecording={true} />}</S.VoiceIndicator>
            </div>
            <Recording
              ref={recordingRef}
              recordingTime={recordingTime}
              onComplete={handleComplete}
              onStartRecording={handleStart}
              canStart={canStart}
            />
          </S.Border>
        </S.Column>
      </S.Wrapper>
      <div className="mt-5">
        {showAnswer && (
          <Answer
            showAnswer={showAnswer}
            audioURL={audioURL}
            showNote={true}
            gradingContent=""
            transcript={true}
            questionId={questionNumber}
            audioBlod={audioBlod}
          />
        )}
      </div>
    </>
  );
};
